"use client";

import { useCallback, useMemo } from "react";

import { useSearchFilterBuilder } from "@/hooks/use-search-filter-builder";
import { getReleaseSearchFilterValues } from "@/lib/facet-filter-state";
import { ALL_MATCH_FILTER_KEYS } from "@/lib/faceted-search-url";
import { normalizeFormatSetSelection } from "@/lib/normalizeFormatSetSelection";
import {
  isReleaseSearchFilterEmpty,
  releaseSearchFilterToFilter,
  type FacetedSearchState,
  type ReleaseSearchFilter,
} from "@garimpa/lib/searchState";

import type { SurfaceConfig } from "./config";

interface UseFacetFiltersOptions {
  state: FacetedSearchState | undefined;
  onChange: (next: FacetedSearchState | undefined) => void;
  surface: SurfaceConfig;
}

function getRange(
  filter: ReleaseSearchFilter | undefined,
  key: string,
): [number | null, number | null] | null {
  if (key !== "year") return null;
  const range = filter?.year;
  if (!range) return null;
  return range.length === 1 ? [range[0], range[0]] : range;
}

function compactState(next: FacetedSearchState): FacetedSearchState | undefined {
  const filter = isReleaseSearchFilterEmpty(next.filter) ? undefined : next.filter;
  const compacted: FacetedSearchState = {};
  if (filter) compacted.filter = filter;
  if (next.sort) compacted.sort = next.sort;
  if (next.page && next.page > 1) compacted.page = next.page;
  if (next.q) compacted.q = next.q;
  return Object.keys(compacted).length ? compacted : undefined;
}

/**
 * Surface-agnostic facet state for the shared <FacetFilterBar>. It edits the
 * public recursive filter contract and exposes the compiled internal Filter
 * array for existing query code.
 */
export function useFacetFilters({ state, onChange, surface }: UseFacetFiltersOptions) {
  const builder = useSearchFilterBuilder(state, onChange);
  const publicFilter = state?.filter;
  const filter = useMemo(
    () =>
      releaseSearchFilterToFilter(publicFilter, {
        allTermKeys: ALL_MATCH_FILTER_KEYS,
      }) ?? [],
    [publicFilter],
  );
  const requestedSort = state?.sort;
  const sort =
    requestedSort && surface.sorts.some((option) => option.value === requestedSort)
      ? requestedSort
      : surface.defaultSort;

  const setSort = useCallback(
    (nextSort: string) => {
      onChange(
        compactState({
          ...state,
          sort: nextSort !== surface.defaultSort ? nextSort : undefined,
          page: undefined,
        }),
      );
    },
    [onChange, state, surface.defaultSort],
  );

  const applyDraft = useCallback(
    (draft: FacetedSearchState | undefined) => {
      onChange(
        compactState({ ...state, filter: draft?.filter, sort: draft?.sort, page: undefined }),
      );
    },
    [onChange, state],
  );

  return {
    state,
    filter,
    sort,
    hasActive: filter.length > 0,
    getValues: useCallback(
      (key: string, availableValues?: readonly string[]) =>
        getReleaseSearchFilterValues(builder.filter, key, availableValues),
      [builder.filter],
    ),
    getRange: useCallback((key: string) => getRange(builder.filter, key), [builder.filter]),
    setValues: useCallback(
      (key: string, values: string[], availableValues?: readonly string[]) => {
        const def = surface.facets.find((facet) => facet.key === key);
        const previousValues = getReleaseSearchFilterValues(builder.filter, key, availableValues);
        const normalizedValues =
          key === "formatSet" ? normalizeFormatSetSelection(previousValues, values) : values;
        const removedValues = previousValues.filter((value) => !normalizedValues.includes(value));
        builder.setValues(key, normalizedValues, {
          match: def?.match ?? "any",
          clearOrValues: removedValues,
        });
      },
      [builder, surface.facets],
    ),
    setRange: useCallback(
      (key: string, range: [number | null, number | null] | null) => {
        if (key !== "year") return;
        builder.setYear(
          range && (range[0] != null || range[1] != null)
            ? range[0] != null && range[1] != null && range[0] === range[1]
              ? [range[0]]
              : [range[0], range[1]]
            : null,
        );
      },
      [builder],
    ),
    clear: builder.clear,
    setSort,
    clearAll: builder.clearAll,
    /** Commits filters and sort edited on a draft copy of `state`, keeping the live search text. */
    applyDraft,
  };
}

export type FacetFiltersController = ReturnType<typeof useFacetFilters>;
